import { couplingListQuerySchema, type CouplingListQuery, type Page, type TruckTrailerCoupling } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useListQuery } from '../../../shared/hooks/use-list-query';
import { toListParams } from '../../../shared/policies/list-params';
import { listCouplings } from '../features/units/units.api';

export type CouplingHistoryState = { status: 'loading' } | { status: 'loaded'; page: Page<TruckTrailerCoupling>; queryKey: string } | { status: 'error' };

async function fetchHistory(vehicleKind: 'truck' | 'trailer', vehicleId: string, listQuery: CouplingListQuery): Promise<CouplingHistoryState> {
  const filter = vehicleKind === 'truck' ? { truckId: [vehicleId] } : { trailerId: [vehicleId] };
  const key = `${vehicleKind}:${vehicleId}?${toListParams(listQuery)}`;
  return { status: 'loaded', page: await listCouplings({ ...listQuery, ...filter }), queryKey: key };
}

/** One vehicle's coupling history, a page at a time; its page and sort live in the URL like every list (CLAUDE.md → Lists and tables). */
export function useCouplingHistory(vehicleKind: 'truck' | 'trailer', vehicleId: string) {
  const list = useListQuery(couplingListQuerySchema);
  const listQuery = list.query;
  const [state, setState] = useState<CouplingHistoryState>({ status: 'loading' });
  const queryKey = `${vehicleKind}:${vehicleId}?${toListParams(listQuery)}`;

  useEffect(() => {
    let cancelled = false;
    fetchHistory(vehicleKind, vehicleId, listQuery)
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [vehicleKind, vehicleId, listQuery]);

  async function reload(): Promise<void> {
    try {
      setState(await fetchHistory(vehicleKind, vehicleId, listQuery));
    } catch {
      setState({ status: 'error' });
    }
  }

  const isRefreshing = state.status === 'loaded' && state.queryKey !== queryKey;
  return { state, list, isRefreshing, reload };
}
